// ---------------------------------------------------------------------------
// SOC-004 — Social Platform Calendar shared types + visual config
// ---------------------------------------------------------------------------
// Mirrors the backend response shape at
// `workers/api-crm/src/routes/social-calendar.ts` (`GET /v1/social/calendar/
// grid`) verbatim. Vocabulary mirrors `@ease/db` SOC_004_SOCIAL_PLATFORM_VOCAB
// + SOC_004_SOCIAL_STATUS_VOCAB + SOC_004_SOCIAL_CALENDAR_VIEW_MODE_VOCAB; the
// frontend keeps its own inline copy so the Vite bundle does NOT pull the
// full `@ease/db` graph (drizzle + neon driver) into the browser. Drift
// between this snapshot and the catalog fails the catalog proof.
//
// Visual config maps every platform to a Stero brand token — no hardcoded hex.
// ---------------------------------------------------------------------------

// lucide-react 1.x dropped most brand-mark icons; using generic glyphs keeps
// the bundle license-clean and stable across upgrades. Each platform picks a
// semantically-meaningful icon (Camera for Instagram, Building for LinkedIn,
// etc.) — the Stero brand-token COLOR carries the per-platform identity.
import {
  AtSign,
  Building,
  Camera,
  Globe,
  Hash,
  Image as ImageIcon,
  Music2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export const SOCIAL_CALENDAR_PLATFORMS = [
  "facebook",
  "instagram",
  "linkedin",
  "tiktok",
  "twitter",
  "youtube",
  "pinterest",
] as const;

export type SocialCalendarPlatform =
  (typeof SOCIAL_CALENDAR_PLATFORMS)[number];

export const SOCIAL_CALENDAR_STATUSES = [
  "draft",
  "scheduled",
  "published",
  "failed",
] as const;

export type SocialCalendarStatus = (typeof SOCIAL_CALENDAR_STATUSES)[number];

export const SOCIAL_CALENDAR_VIEW_MODES = ["month", "week", "list"] as const;

export type SocialCalendarViewMode =
  (typeof SOCIAL_CALENDAR_VIEW_MODES)[number];

export interface SocialCalendarItem {
  id: string;
  postId: string;
  date: string;
  dayKey: string;
  platform: SocialCalendarPlatform;
  accountName: string;
  title: string;
  body: string;
  status: SocialCalendarStatus;
}

export interface SocialCalendarDayBucket {
  dayKey: string;
  count: number;
  itemIds: string[];
}

export interface SocialCalendarGridResponse {
  data: {
    items: SocialCalendarItem[];
    days: SocialCalendarDayBucket[];
    platforms: SocialCalendarPlatform[];
    statuses: SocialCalendarStatus[];
    viewMode: SocialCalendarViewMode;
    from: string;
    to: string;
    telemetry: {
      type: "social_calendar.viewed@v1";
      payload: {
        tenantId: string;
        fromDate: string;
        toDate: string;
        requestedPlatforms: SocialCalendarPlatform[] | undefined;
        requestedStatuses: SocialCalendarStatus[] | undefined;
        resultCount: number;
        viewMode: SocialCalendarViewMode;
        queriedAt: string;
      };
    };
  };
}

export interface SocialPlatformVisual {
  label: string;
  icon: LucideIcon;
  badgeClass: string;
  dotClass: string;
}

export const SOCIAL_PLATFORM_VISUALS: Record<
  SocialCalendarPlatform,
  SocialPlatformVisual
> = {
  facebook: {
    label: "Facebook",
    icon: Globe,
    badgeClass:
      "bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30",
    dotClass: "bg-[var(--color-primary)]",
  },
  instagram: {
    label: "Instagram",
    icon: Camera,
    badgeClass:
      "bg-[color:color-mix(in_oklab,var(--color-secondary,#7C3AED)_12%,transparent)] text-[var(--color-secondary,#7C3AED)] border-[color:color-mix(in_oklab,var(--color-secondary,#7C3AED)_30%,transparent)]",
    dotClass: "bg-[var(--color-secondary,#7C3AED)]",
  },
  linkedin: {
    label: "LinkedIn",
    icon: Building,
    badgeClass:
      "bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30",
    dotClass: "bg-[var(--color-primary)]",
  },
  tiktok: {
    label: "TikTok",
    icon: Music2,
    badgeClass:
      "bg-[var(--color-muted)] text-[var(--color-foreground)] border-[var(--color-border)]",
    dotClass: "bg-[var(--color-foreground)]",
  },
  twitter: {
    label: "X / Twitter",
    icon: AtSign,
    badgeClass:
      "bg-[var(--color-muted)] text-[var(--color-foreground)] border-[var(--color-border)]",
    dotClass: "bg-[var(--color-foreground)]",
  },
  youtube: {
    label: "YouTube",
    icon: ImageIcon,
    badgeClass:
      "bg-[color:color-mix(in_oklab,var(--color-destructive,#dc2626)_12%,transparent)] text-[var(--color-destructive,#dc2626)] border-[color:color-mix(in_oklab,var(--color-destructive,#dc2626)_30%,transparent)]",
    dotClass: "bg-[var(--color-destructive,#dc2626)]",
  },
  pinterest: {
    label: "Pinterest",
    icon: Hash,
    badgeClass:
      "bg-[color:color-mix(in_oklab,var(--color-destructive,#dc2626)_12%,transparent)] text-[var(--color-destructive,#dc2626)] border-[color:color-mix(in_oklab,var(--color-destructive,#dc2626)_30%,transparent)]",
    dotClass: "bg-[var(--color-destructive,#dc2626)]",
  },
};

export interface SocialStatusVisual {
  label: string;
  badgeClass: string;
}

export const SOCIAL_STATUS_VISUALS: Record<
  SocialCalendarStatus,
  SocialStatusVisual
> = {
  draft: {
    label: "Draft",
    badgeClass:
      "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] border-[var(--color-border)]",
  },
  scheduled: {
    label: "Scheduled",
    badgeClass:
      "bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30",
  },
  published: {
    label: "Published",
    badgeClass:
      "bg-[var(--color-success)]/10 text-[var(--color-success)] border-[var(--color-success)]/30",
  },
  failed: {
    label: "Failed",
    badgeClass:
      "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)] border-[var(--color-destructive)]/30",
  },
};

// ---------------------------------------------------------------------------
// Date helpers — pure, no React, no DOM, easily unit-tested.
// ---------------------------------------------------------------------------

export function dateInputToIsoStart(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function dateInputToIsoEnd(value: string): string {
  return new Date(`${value}T23:59:59.999Z`).toISOString();
}

/**
 * Default month range: first → last day of the current month, in
 * yyyy-mm-dd format suitable for `<input type="date">` binding.
 */
export function defaultMonthRange(now: Date = new Date()): {
  from: string;
  to: string;
  anchor: string;
} {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0),
  );
  return {
    from: start.toISOString().slice(0, 10),
    to: end.toISOString().slice(0, 10),
    anchor: start.toISOString().slice(0, 10),
  };
}

/**
 * Builds the 6×7 day grid for a given month anchor (yyyy-mm-dd of the
 * first of the month). Each cell carries `dayKey` (yyyy-mm-dd) + a flag
 * for whether it belongs to the anchor month (vs leading/trailing
 * neighbour-month padding). Always returns 42 cells so the grid layout
 * does NOT reflow as users page months.
 */
export interface MonthGridCell {
  dayKey: string;
  inMonth: boolean;
  dayOfMonth: number;
}

export function buildMonthGrid(anchorIsoDate: string): MonthGridCell[] {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth();
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  // ISO week starts Monday in many locales but the audit row doesn't
  // pin a weekday convention. Use Sunday = 0 here to match common
  // North-American calendar UI; can be themed later via a tenant pref.
  const leadingDays = firstOfMonth.getUTCDay();
  const cells: MonthGridCell[] = [];
  for (let offset = 0; offset < 42; offset += 1) {
    const cellDate = new Date(
      Date.UTC(year, month, 1 - leadingDays + offset),
    );
    cells.push({
      dayKey: cellDate.toISOString().slice(0, 10),
      inMonth: cellDate.getUTCMonth() === month,
      dayOfMonth: cellDate.getUTCDate(),
    });
  }
  return cells;
}

/** Step the anchor by ±1 month. Returns yyyy-mm-dd of the new first-of-month. */
export function stepMonth(anchorIsoDate: string, delta: number): string {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  const next = new Date(
    Date.UTC(
      anchor.getUTCFullYear(),
      anchor.getUTCMonth() + delta,
      1,
    ),
  );
  return next.toISOString().slice(0, 10);
}

/** Computes the month boundary range for a given anchor (first → last day). */
export function monthRange(anchorIsoDate: string): {
  from: string;
  to: string;
} {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  const last = new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0),
  );
  return {
    from: anchor.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
  };
}

/** Formats an ISO datetime for the post-card chip (HH:MM in UTC). */
export function formatItemTime(iso: string): string {
  return iso.slice(11, 16);
}

/** Formats yyyy-mm-dd as a human month label e.g. "May 2026". */
export function formatMonthLabel(anchorIsoDate: string): string {
  const anchor = new Date(`${anchorIsoDate}T00:00:00.000Z`);
  if (Number.isNaN(anchor.getTime())) return anchorIsoDate;
  return anchor.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
